import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import * as Sentry from '@sentry/nextjs';

import { ApiBackendSingleResponse } from '@nf/types/api';
import { 
	WcPromotionSpinDailyInfoResponse,
	WcSpinCompletedTask,
	WcPromotionSpinTasksCompleteResponse 
} from '../types';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useApiBackendInfo } from '@/hooks/query/use-api-backend-info';
import queryKeys from '@/constants/query-keys';

interface SpinTasksCompletePayload {
	Tasks: Array<WcSpinCompletedTask>;
}

export const useWcPromotionSpinTasksComplete = (options?: {
	onSuccess?: (data: WcPromotionSpinTasksCompleteResponse | null) => void
}) => {
	const queryClient = useQueryClient();
	const apiBackendInfo = useApiBackendInfo();
	const dailyInfoQueryKey = [queryKeys.WC_PROMOTION_SPIN_DAILY_INFO, apiBackendInfo.url, apiBackendInfo.token];

	return useMutation({
		mutationFn: async (
			body: SpinTasksCompletePayload
		): Promise<ApiBackendSingleResponse<WcPromotionSpinTasksCompleteResponse>> => {
			const response = await fetchComposer.postWithBearer(`${apiBackendInfo.url}/spin/tasks/complete`, {
				body: JSON.stringify(body)
			})(apiBackendInfo.token);
			
			if (!response.ok) {
				throw new Error(`${response.status}: ${response.statusText}`);
			}

			const data: ApiBackendSingleResponse<WcPromotionSpinTasksCompleteResponse> = await response.json();
			
			if (data.ErrorCode !== 0) {
				Sentry.captureMessage('spin/tasks/complete failed', {
					level: 'error',
					tags: {
						api: 'spin/tasks/complete',
						errorCode: String(data.ErrorCode),
					},
					extra: {
						errorMessage: data.ErrorMessage,
						tasks: body.Tasks,
						response: data,
					},
				});

				throw new Error(data.ErrorMessage);
			}

			return data;
		},
		onMutate: async (variables: SpinTasksCompletePayload) => {
			await queryClient.cancelQueries({ queryKey: dailyInfoQueryKey });
			const previousData = queryClient.getQueryData<
				ApiBackendSingleResponse<WcPromotionSpinDailyInfoResponse>
			>(dailyInfoQueryKey);
			
			queryClient.setQueryData(
				dailyInfoQueryKey, 
				(old: ApiBackendSingleResponse<WcPromotionSpinDailyInfoResponse> | undefined) => {
					if (!old || !old.Data) return old;

					return produce(old, draft => {
						if (!draft.Data) return;
						
						const today = draft.Data[0];
						today.tasks.forEach(task => {
							if (('clickTaskType' in task && task.clickTaskType != null) &&
								variables.Tasks.some(t => t.clickTaskType === task.clickTaskType)
							) {
								task.isCompleted = true;
							} else if (('betType' in task && task.betType != null) && 
								variables.Tasks.some(t => t.betType === task.betType)
							) {
								task.isCompleted = true;
							}
						});
					});
				}
			);

			return { previousData };
		},
		onSuccess: (data) => {
			options?.onSuccess?.(data.Data);
		},
		onError: (err, variables, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(dailyInfoQueryKey, context.previousData);
			}
		},
	});
};